import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

/**
 * /auth/callback — handles the redirect back from Emergent's Google OAuth.
 *
 * Two flows depending on `flowup_delete_intent` flag:
 *   1) Normal login: exchange session_id for FlowUp JWT, then route to
 *      /{slug} (owner) — NEVER fall back to /S.ADMIN for non-super-admins.
 *   2) Account deletion: verify Google session, show "Are you sure?"
 *      confirmation, then call the delete endpoint and bounce to home.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS.
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { googleSession } = useAuth();
  const hasProcessed = useRef(false);
  // processing | confirm-delete | deleting | done | error
  const [status, setStatus] = useState("processing");
  const [errorMsg, setErrorMsg] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null); // { session_id, email }

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      setStatus("error");
      setErrorMsg("לא נמצא session_id בכתובת — נסה להתחבר שוב");
      return;
    }
    const sessionId = decodeURIComponent(m[1]);

    // Was this OAuth flow started by "מחק חשבון לצמיתות"?
    let deleteIntent = false;
    try {
      deleteIntent = sessionStorage.getItem("flowup_delete_intent") === "1";
      sessionStorage.removeItem("flowup_delete_intent");
    } catch { /* no-op */ }

    // Clean the URL fragment so refreshes don't re-trigger
    window.history.replaceState(null, "", "/auth/callback");

    if (deleteIntent) {
      // STEP 1: Don't delete yet — first show a confirmation card.
      // We pass session_id forward so the actual delete call only fires
      // when the user explicitly clicks "כן, מחק".
      setPendingDelete({ session_id: sessionId });
      setStatus("confirm-delete");
      return;
    }

    // Normal login flow
    (async () => {
      try {
        const { user, isNew } = await googleSession(sessionId);
        // Resolve where to land. Owners ALWAYS go to their /{slug} — never
        // to /S.ADMIN (super-admin grid). If we can't resolve a slug we
        // bounce to /login rather than expose the admin grid.
        let target = null;
        if (user.role === "super_admin") {
          target = "/S.ADMIN";
        } else {
          try {
            const { data } = await api.get("/companies");
            const own = (data.companies || []).find((c) => c.id === user.company_id);
            if (own?.slug) target = `/${own.slug}`;
          } catch { /* ignore */ }
        }
        if (!target) {
          setStatus("error");
          setErrorMsg("לא נמצא דשבורד פעיל. אנא נסה שוב להתחבר.");
          return;
        }
        setStatus("done");
        setTimeout(() => navigate(target, { replace: true, state: { isNew } }), 250);
      } catch (e) {
        setStatus("error");
        setErrorMsg(e?.response?.data?.detail || "שגיאה בהתחברות לגוגל");
      }
    })();
  }, [googleSession, navigate]);

  const confirmDelete = async () => {
    if (!pendingDelete?.session_id) return;
    setStatus("deleting");
    try {
      await api.post("/auth/delete-by-google-session", {
        session_id: pendingDelete.session_id,
      });
      try {
        localStorage.removeItem("flowup_auth_token");
        sessionStorage.removeItem("flowup_auth_token");
      } catch { /* no-op */ }
      setStatus("done");
      setTimeout(() => navigate("/?account=deleted", { replace: true }), 600);
    } catch (e) {
      setStatus("error");
      setErrorMsg(e?.response?.data?.detail || "מחיקת חשבון נכשלה");
    }
  };

  const cancelDelete = () => {
    setPendingDelete(null);
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-bl from-brand-50 via-white to-stone-50" dir="rtl">
      <div className="max-w-sm w-full bg-white border border-stone-200 rounded-2xl p-8 shadow-md text-center space-y-3" data-testid="auth-callback-card">
        {status === "processing" && (
          <>
            <Loader2 className="h-8 w-8 text-brand mx-auto animate-spin" />
            <h1 className="font-heading font-bold text-lg">מתחבר...</h1>
            <p className="text-sm text-stone-500">מאמת מול Google</p>
          </>
        )}

        {status === "confirm-delete" && (
          <>
            <div className="h-14 w-14 rounded-full bg-rose-100 mx-auto flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-rose-600" />
            </div>
            <h1 className="font-heading font-bold text-xl text-rose-700">האם אתה בטוח?</h1>
            <p className="text-sm text-stone-600 leading-relaxed">
              אימתת את הזהות שלך דרך Google.<br />
              לחיצה על "כן, מחק" תמחק את העסק שלך, ההיסטוריה והנתונים — <strong className="text-rose-600">לצמיתות</strong>.
            </p>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={confirmDelete}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white font-extrabold py-2.5 rounded-lg shadow-md"
                data-testid="confirm-delete-yes"
              >
                <Trash2 className="h-4 w-4" />
                כן, מחק
              </button>
              <button
                type="button"
                onClick={cancelDelete}
                className="flex-1 bg-white hover:bg-stone-50 border-2 border-stone-300 text-stone-700 font-bold py-2.5 rounded-lg"
                data-testid="confirm-delete-no"
              >
                לא, חזרה לכניסה
              </button>
            </div>
          </>
        )}

        {status === "deleting" && (
          <>
            <Loader2 className="h-8 w-8 text-rose-500 mx-auto animate-spin" />
            <h1 className="font-heading font-bold text-lg text-rose-700">מוחק חשבון...</h1>
          </>
        )}

        {status === "done" && (
          <>
            <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto" />
            <h1 className="font-heading font-bold text-lg">הצלחה!</h1>
            <p className="text-sm text-stone-500">מעביר אותך הלאה...</p>
          </>
        )}

        {status === "error" && (
          <>
            <AlertTriangle className="h-8 w-8 text-rose-500 mx-auto" />
            <h1 className="font-heading font-bold text-lg">משהו השתבש</h1>
            <p className="text-sm text-rose-600">{errorMsg}</p>
            <button
              type="button"
              onClick={() => navigate("/login", { replace: true })}
              className="mt-2 text-sm text-brand font-bold hover:underline"
              data-testid="auth-callback-back"
            >
              חזרה למסך התחברות
            </button>
          </>
        )}
      </div>
    </div>
  );
}
